import * as yup from 'yup';
import { startOfDay } from 'date-fns';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import {
  AddDiscountMutation as AddDiscount,
  DiscountStrategy,
  DiscountType,
  GetDiscountsQuery as GetDiscounts,
} from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddDiscountMutation from './AddDiscountMutation';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useValidation from '../../../../common/useValidation';

type Event = GetDiscounts['project']['event'];
type AddedDiscount = AddDiscount['addDiscount'];

const addDiscountFormSchema = yup.object().shape({
  title: yup.string().required(),
});

export interface AddDiscountFormProps {
  event: Event;
  onSuccess?: (discount: AddedDiscount) => void;
}

const AddDiscountForm = ({ event, onSuccess }: AddDiscountFormProps) => {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      event: { id: event.id },
      title: '',
      strategy: DiscountStrategy.scheduled,
      type: DiscountType.absolute,
      start_date: formatDate(
        startOfDay(new Date()),
        { format: 'internal_date_time', timezone: 'UTC' },
      ),
      end_date: null,
      levels: [],
      active: !event.active,
    },
  });

  const [addDiscount, { error, loading }] = useMutation(AddDiscountMutation, {
    variables: {
      input: form.values,
    },
    onCompleted: ({ addDiscount }) => {
      notifier.success(t('notifications.discount_added'));
      onSuccess?.(addDiscount);
    },
    onError: () => {
      //
    },
  });

  const { valid, errors } = useValidation({
    schema: addDiscountFormSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addDiscount()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_discount')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_discount_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup
          sc={{ valid: !errors.title, touched: form.touched.title }}
        >
          <UI.InputLabel htmlFor="DiscountPrefix">
            {t('discount_title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            id="DiscountPrefix"
            {...form.text('title')}
            placeholder={t('discount_title_placeholder')}
            autoFocus
          />
          <UI.ErrorMessages attribute={t('discount_title')} errors={errors.title} />
        </UI.InputGroup>
      </UI.FormGrid>

      <UI.HR />

      <UI.FormButtons
        form={form}
        submitLabel={t('add_discount')}
        loading={loading}
        valid={valid}
      />
    </UI.Form>
  );
};

export default AddDiscountForm;
